import React from 'react';
import { TYPE_OF_FLOW } from '../../../../../../../utility/constants';

// Función para formatear números - EXACTAMENTE como en mensual
export const formatCurrency = (value, currency, isNegative = false) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    const numericValue = parseFloat(value) || 0;
    const absValue = Math.abs(numericValue);
    const formatted = currency === 'USD'
        ? new Intl.NumberFormat('en-US', { 
            style: 'currency', 
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(absValue)
        : new Intl.NumberFormat('es-GT', { 
            style: 'currency', 
            currency: 'GTQ',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(absValue);
    
    // Para salidas, siempre mostrar como negativo con paréntesis
    const isActuallyNegative = isNegative || numericValue < 0;
    const displayValue = isActuallyNegative ? `(${formatted})` : formatted;
    
    return isActuallyNegative ? 
        <span style={{ color: '#dc3545', fontWeight: '500' }}>{displayValue}</span> : 
        displayValue;
};

// Función para formatear inversiones (siempre entre paréntesis)
export const formatInvestmentCurrency = (value, currency) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '';
    }
    
    const absValue = Math.abs(parseFloat(value) || 0);
    let formatted;
    
    if (currency === 'USD') {
        formatted = new Intl.NumberFormat('en-US', { 
            style: 'currency', 
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(absValue);
    } else {
        formatted = new Intl.NumberFormat('es-GT', { 
            style: 'currency', 
            currency: 'GTQ',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(absValue);
    }
    
    return `(${formatted})`;
};

// Componente para mostrar valores con formato y color automático
export const CurrencyDisplay = ({ value, currency, isSalida, isInvestment = false }) => {
    if (value === null || value === undefined || isNaN(value)) {
        return <span>-</span>;
    }
    
    const numericValue = parseFloat(value) || 0;
    
    let formattedValue;
    let color = '#000';
    let fontWeight = '400';
    
    if (isInvestment) {
        formattedValue = formatInvestmentCurrency(value, currency);
        color = '#dc3545';
        fontWeight = '500';
    } else {
        formattedValue = formatCurrency(value, currency, isSalida);
        
        if (numericValue < 0 || isSalida) {
            color = '#dc3545'; // Rojo para salidas
        } else {
            color = ''; // sin definir color para entradas
        }
        fontWeight = '500';
    }
    
    return (
        <span style={{ color, fontWeight }}>
            {formattedValue}
        </span>
    );
};

// Función para detectar si una fila es salida
export const isSalidaRow = (row) => {
    if (!row) return false;
    
    // Usar las constantes TYPE_OF_FLOW del backend
    return row.type_of_flow === TYPE_OF_FLOW.SALIDAS || // 40 - EXPENDITURES
           row.type_of_flow === 40 || // Asegurarse compatibilidad numérica
           (row.name && (
               row.name.includes("PAGOS") || 
               row.name.includes("SALIDAS") || 
               row.name.includes("PAGO")
           ));
};

// Función para separar campos por tipo - Corregida para manejar estructura del backend
export const separateFieldsByTypeAnnual = (fields) => {
    const allFields = fields.getAll() || [];
    const ingresos = [];
    const salidas = [];

    allFields.forEach((field, index) => {
        // Los elementos con isTitulo son importantes - contienen totales por sección del backend
        if (field.isTitulo) {
            // Determinar si es título de INGRESOS o SALIDAS basado en el nombre
            if (field.name === 'INGRESOS') {
                ingresos.push({ field, index, isTitulo: true });
            } else if (field.name === 'SALIDAS') {
                salidas.push({ field, index, isTitulo: true });
            }
            return;
        }
        
        // Para elementos normales, usar type_of_flow
        if (field.type_of_flow === TYPE_OF_FLOW.SALIDAS || field.type_of_flow === 40) {
            salidas.push({ field, index });
        } else if (field.type_of_flow === TYPE_OF_FLOW.INGRESOS || field.type_of_flow === 30) {
            ingresos.push({ field, index });
        } else {
            // Si no tiene type_of_flow definido, usar la función de detección por nombre
            if (isSalidaRow(field)) {
                salidas.push({ field, index });
            } else {
                ingresos.push({ field, index });
            }
        }
    });

    return { ingresos, salidas };
};

// Función principal para calcular totales una vez al cargar - Corregida para usar TYPE_OF_FLOW correctamente (SIN totales de fila)
export const calculateAnnualTotals = (allValues, yearList) => {
    if (!allValues || !yearList || yearList.length === 0) {
        const empty = {};
        if (yearList && yearList.length > 0) {
            yearList.forEach(year => empty[year] = 0);
        }
        
        return {
            entradas: { ...empty },
            salidas: { ...empty },
            flujoNeto: { ...empty },
            saldoFinal: { ...empty }
        };
    }
    
    // Inicializar estructura de totales (sin .total)
    const summaryTotals = {
        entradas: {},
        salidas: {},
        flujoNeto: {},
        saldoFinal: {}
    };
    
    // Inicializar cada año
    yearList.forEach(year => {
        summaryTotals.entradas[year] = 0;
        summaryTotals.salidas[year] = 0;
        summaryTotals.flujoNeto[year] = 0;
        summaryTotals.saldoFinal[year] = 0;
    });
    
    // Función helper para procesar items de cada sección
    const processItems = (items) => {
        if (!items || !Array.isArray(items)) return;
        
        items.forEach(item => {
            // Saltear elementos con isTitulo o is_not_suma
            if (item.isTitulo || item.is_not_suma) {
                return;
            }
            
            yearList.forEach(year => {
                const valor = parseFloat(item[year]) || 0;
                
                // Usar las constantes TYPE_OF_FLOW correctamente
                if (item.type_of_flow === TYPE_OF_FLOW.SALIDAS || item.type_of_flow === 40) {
                    // Es una salida (expenditure) - siempre tratar como negativa
                    summaryTotals.salidas[year] += Math.abs(valor); // Sumar el valor absoluto
                } else if (item.type_of_flow === TYPE_OF_FLOW.INGRESOS || item.type_of_flow === 30) {
                    // Es una entrada (income) - siempre tratar como positiva
                    summaryTotals.entradas[year] += Math.abs(valor);
                } else {
                    // Si no tiene type_of_flow, usar detección por nombre/valor
                    if (isSalidaRow(item)) {
                        // Es una salida - tratar como negativa
                        summaryTotals.salidas[year] += Math.abs(valor);
                    } else {
                        // Es una entrada - tratar como positiva
                        summaryTotals.entradas[year] += Math.abs(valor);
                    }
                }
            });
        });
    };
    
    // Procesar todas las secciones
    if (allValues.operation) {
        processItems(allValues.operation);
    }
    if (allValues.financial) {
        processItems(allValues.financial);
    }
    if (allValues.investments && allValues.investments.results) {
        // Las inversiones siempre son salidas
        allValues.investments.results.forEach(item => {
            if (item.isTitulo || item.is_not_suma) return;
            
            yearList.forEach(year => {
                const valor = parseFloat(item[year]) || 0;
                summaryTotals.salidas[year] += Math.abs(valor); // Sumar el valor absoluto
            });
        });
    }
    
    // Calcular flujo neto por año (entradas - salidas)
    yearList.forEach(year => {
        summaryTotals.flujoNeto[year] = summaryTotals.entradas[year] - summaryTotals.salidas[year];
    });
    
    // Calcular saldo final de forma acumulativa
    let saldoAcumulado = 0;
    
    yearList.forEach((year, index) => {
        if (index === 0) {
            // Para el primer año: Saldo Inicial (si existe) + Flujo Neto
            const saldoInicial = parseFloat(
                allValues.summary && allValues.summary[0] && allValues.summary[0][year] 
                    ? allValues.summary[0][year] 
                    : 0
            ) || 0;
            saldoAcumulado = saldoInicial + summaryTotals.flujoNeto[year];
        } else {
            // Para años siguientes: Saldo Final del año anterior + Flujo Neto del año actual
            saldoAcumulado = saldoAcumulado + summaryTotals.flujoNeto[year];
        }
        
        summaryTotals.saldoFinal[year] = saldoAcumulado;
    });
    
    return summaryTotals;
};

// Hook para calcular totales una vez al cargar
export const useAnnualTotals = (allValues, yearList) => {
    return React.useMemo(() => {
        return calculateAnnualTotals(allValues, yearList);
    }, [allValues, yearList]);
};

// Función para calcular totales por sección - Corregida para manejar estructura del backend (SIN totales de fila)
export const calculateSectionTotalsAnnual = (sectionFields, yearList) => {
    const totals = {};
    yearList.forEach(year => totals[year] = 0);
    
    sectionFields.forEach(({ field, isTitulo }) => {
        if (isTitulo) {
            // Los elementos con isTitulo ya contienen los totales calculados por el backend
            yearList.forEach(year => {
                const value = parseFloat(field[year]) || 0;
                totals[year] = value; // Usar directamente el valor del backend
            });
        } else {
            // Para elementos normales, sumar los valores absolutos
            yearList.forEach(year => {
                const value = parseFloat(field[year]) || 0;
                totals[year] += Math.abs(value); // Sumar el valor absoluto
            });
        }
    });
    
    return totals; // No calcular total de fila
};

// Función para renderizar headers de sección - SIN columna total
export const renderSectionHeaderAnnual = (title, bgColor = "#E8F4FD", sectionFields = [], currency, yearList = []) => {
    const sectionTotals = calculateSectionTotalsAnnual(sectionFields, yearList);
    const isSalidaSection = title === 'SALIDAS';
    const titleStyle = isSalidaSection ? 
        { fontWeight: 'bold', fontSize: '14px', padding: '8px 12px', color: '#dc3545 !important' } : 
        { fontWeight: 'bold', fontSize: '14px', padding: '8px 12px' };
    
    return (
        <tr style={{ backgroundColor: bgColor }}>
            <td className='first-column table-form-fixed-cell-left-0 titulo-left-padded' 
                style={{ 
                    ...titleStyle,
                    borderLeft: `4px solid ${title === 'INGRESOS' ? '#28a745' : '#dc3545'}`
                }}>
                <span style={isSalidaSection ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                    {title}
                </span>
            </td>
            {yearList.map((year, idx) => (
                <td key={idx} className='text-right-padded' style={{ fontWeight: 'bold' }}>
                    {formatCurrency(sectionTotals[year], currency, isSalidaSection)}
                </td>
            ))}
        </tr>
    );
};

// Función para obtener estilos de celda según el tipo
export const getCellStyles = (isSalida, isFinal = false) => {
    const baseStyle = {
        width: "8%",
        ...(isSalida ? { color: '#dc3545', fontWeight: '500' } : { color: '#28a745', fontWeight: '500' })
    };
    
    if (isFinal) {
        return {
            ...baseStyle,
            backgroundColor: "#F0FFC7"
        };
    }
    
    return baseStyle;
}; 